import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Minus, Plus, ShoppingBag } from 'lucide-react';

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="bg-black text-white min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="mx-auto text-[#909090] mb-4" size={48} />
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-zinc-400 mb-6">Browse our lab-tested research compounds.</p>
          <Link to="/shop" className="inline-block bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-7 py-3 rounded transition-colors">
            Return to shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-8">Cart</h1>
        <div className="grid lg:grid-cols-[1fr_380px] gap-10">
          <div>
            <div className="hidden md:grid grid-cols-[80px_1fr_120px_120px_60px] gap-4 pb-3 border-b border-zinc-900 text-xs uppercase tracking-wider text-zinc-500">
              <div></div>
              <div>Product</div>
              <div>Price</div>
              <div>Quantity</div>
              <div></div>
            </div>
            <ul className="divide-y divide-zinc-900">
              {items.map((item) => (
                <li key={item.id} className="grid grid-cols-[80px_1fr_60px] md:grid-cols-[80px_1fr_120px_120px_60px] gap-4 py-5 items-center">
                  <div className="bg-zinc-950 border border-zinc-900 rounded p-1">
                    <img src={item.image} alt={item.name} className="w-full aspect-square object-contain" />
                  </div>
                  <div>
                    <Link to={`/product/${item.slug}`} className="text-white hover:text-[#909090] font-medium text-sm">
                      {item.name}
                    </Link>
                    <p className="md:hidden text-[#909090] mt-1 font-bold">€ {item.price.toFixed(2)}</p>
                  </div>
                  <div className="hidden md:block text-[#909090] font-semibold">€ {item.price.toFixed(2)}</div>
                  <div className="col-span-2 md:col-span-1 flex items-center">
                    <div className="flex items-center border border-zinc-800 rounded">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} className="px-2 py-2 text-zinc-400 hover:text-[#909090]">
                        <Minus size={14} />
                      </button>
                      <span className="px-3 text-white text-sm">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} className="px-2 py-2 text-zinc-400 hover:text-[#909090]">
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.id)} className="text-zinc-500 hover:text-red-400 justify-self-end">
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>

            <div className="flex justify-between mt-6">
              <Link to="/shop" className="text-sm text-zinc-400 hover:text-[#909090]">← Continue shopping</Link>
              <button onClick={clearCart} className="text-sm text-zinc-500 hover:text-red-400">Clear cart</button>
            </div>
          </div>

          <aside className="bg-zinc-950 border border-zinc-900 rounded p-6 h-fit">
            <h2 className="text-xl font-bold mb-5">Order summary</h2>
            <div className="space-y-3 text-sm pb-5 border-b border-zinc-900">
              <div className="flex justify-between"><span className="text-zinc-400">Subtotal</span><span>€ {subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Shipping</span><span className="text-zinc-300">Calculated at checkout</span></div>
              <div className="flex justify-between text-green-400"><span>Discount (5%)</span><span>− € {(subtotal * 0.05).toFixed(2)}</span></div>
            </div>
            <div className="flex justify-between items-baseline py-5 border-b border-zinc-900">
              <span className="text-lg font-semibold">Total</span>
              <span className="text-2xl font-bold text-[#909090]">€ {(subtotal * 0.95).toFixed(2)}</span>
            </div>
            <Link to="/checkout" className="mt-5 block text-center bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold py-3.5 rounded transition-colors">
              Proceed to checkout
            </Link>
            <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
              Secure checkout. Worldwide shipping. COA included with every order.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
